import React, { useMemo, useCallback } from 'react';
import { StatusBar, StyleSheet, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAppSelector } from '@/store';
import { UserType } from '@/types/residents/user';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { GradientBackground, FAQContent } from '@/components/residents/redesign';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';
import { FaqSection, faqSectionsGuarantor, faqSectionsResident } from '@/screens/residents/faq/faq';
import { AppConfig } from '@/constants/residents/config';
import { strings } from '@/constants/residents';
import { RootStackParamList } from '@/navigation/RootNavigator';

export const HowItWorksScreen: React.FC = () => {
  const { user } = useAppSelector((s) => s.auth);
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();
  const insets = useSafeAreaInsets();

  const sections = useMemo<FaqSection[]>(() => {
    if (!user) return faqSectionsResident;
    return user.type === UserType.RESIDENT ? faqSectionsResident : faqSectionsGuarantor;
  }, [user]);

  const handleBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const onSupportPress = useCallback(() => {
    const base = AppConfig.URLS.FEEDBACK;
    const email = user?.email;
    const url = email ? `${base}?email=${encodeURIComponent(email)}` : base;
    navigation.navigate('WebView', { url, title: strings.profile.option.feedback });
  }, [user?.email, navigation]);

  return (
    <GradientBackground>
      <StatusBar barStyle="light-content" />
      <BaseResidentMobileScreen
        screenName="HowItWorks"
        darkBackground
        headerVariant="gradient"
      >
        <View style={styles.backLinkWrapper}>
          <BackLink
            label="Back to settings"
            onPress={handleBack}
            color={PRIMITIVE_COLORS.resioBlue}
          />
        </View>
        <FAQContent
          sections={sections}
          onSupportPress={onSupportPress}
          testIdPrefix="HowItWorks"
          contentPaddingBottom={40 + insets.bottom}
        />
      </BaseResidentMobileScreen>
    </GradientBackground>
  );
};

const styles = StyleSheet.create({
  backLinkWrapper: {
    paddingHorizontal: 24,
    paddingTop: 8,
  },
});

export default HowItWorksScreen;
